import { createApi } from '@reduxjs/toolkit/query/react'
import { rolesApi } from 'api/roles'
import { tutorialApi } from 'api/tutorial'
import { AxiosError } from 'axios'
import { LOCAL_STORAGE_CLIENT_ID } from 'shared/constants/localStorageVariables'
import {
  GET_USER_NOTIFICATIONS_TAG,
  GET_USER_SETTINGS_TAG,
} from 'shared/constants/storeTags'
import { NO_ACCESS_PATH } from 'shared/constants/urls'
import { SimpleClient } from 'shared/models/Client'
import {
  IActiveNotification,
  IHandleNotification,
  IHandleSetting,
  INotification,
  IUser,
  IUserSettings,
} from 'shared/models/User'
import axiosBaseQuery from 'shared/utils/axiosBaseQuery'
import { getClientIdsForUser } from 'shared/utils/rolesHelper'
import { mapNotifications } from 'shared/utils/user-helper'
import { setUserClients } from '../clients'
import { setClientById } from '../clients/actions'

const USER_SETTING_BASE_URL = 'user/settings'

const headers = {
  'x-api-version': '2.0',
}

const userApi = createApi({
  reducerPath: 'user',
  tagTypes: [GET_USER_SETTINGS_TAG, GET_USER_NOTIFICATIONS_TAG],
  baseQuery: axiosBaseQuery({ baseUrl: '' }),
  endpoints: (build) => ({
    /** User and roles */
    getUser: build.query<IUser, void>({
      async queryFn(_arg, { dispatch }, _extraOptions, query) {
        const person = await query({
          url: 'person/Person',
          method: 'GET',
          headers,
        })

        if (person.error) {
          const axiosErr = person.error as AxiosError
          if (axiosErr.status === 404) {
            // ai.appInsights?.trackTrace({
            //   message: 'Claim not set, redirecting to no access page.',
            // })
            if (window.location.hostname === 'localhost') {
              window.location.href = `https://${window.config.bblCode}.aztest.bbl.no/${NO_ACCESS_PATH}${window.location.origin}/portal/logout`
              return
            }
            window.location.href = `${window.location.origin}/${NO_ACCESS_PATH}${window.location.origin}/portal/logout`
          }

          return { error: person.error }
        }

        const { data: roles } = await dispatch(
          rolesApi.endpoints.getRole.initiate({})
        )

        let clientId: string | null = (await localStorage.getItem(
          LOCAL_STORAGE_CLIENT_ID
        )) as string

        const distinctClientIds = getClientIdsForUser(roles)

        const clientsForUser = await dispatch(
          userApi.endpoints.getClientsForUser.initiate(distinctClientIds)
        )

        dispatch(setUserClients(clientsForUser.data || []))

        if (!distinctClientIds.includes(clientId)) {
          localStorage.removeItem(LOCAL_STORAGE_CLIENT_ID)
          clientId = null
        }

        dispatch(tutorialApi.util.invalidateTags(['Tutorial']))

        // Gets clientId based on already set clientId from localStorage, or the new passed in.
        // Or if only one is available
        if (clientId || distinctClientIds.length === 1) {
          const pickedId = clientId || distinctClientIds[0]
          await dispatch(setClientById(pickedId))
        }

        await dispatch(userApi.endpoints.getUserSettings.initiate())

        const data = {
          ...(person.data as Partial<IUser>),
        } as IUser

        return { data }
      },
    }),

    getClientsForUser: build.query<SimpleClient[], string[]>({
      async queryFn(distinctClients, _api, _extraOptions, query) {
        let url = 'client/minimizedClients'

        // Optimize the minimized clients call by only calling it with the ids we need
        // But only if the clients is under 10.
        // Otherwise we just fetch all of them under the bbl.
        if (distinctClients.length < 10) {
          const ids = distinctClients.join('&ids=')
          url += `?ids=${ids}`
        }

        const simpleClients = await query({
          url,
          method: 'GET',
        })

        if (simpleClients.error) {
          return { error: simpleClients.error }
        }

        const withAddedSortKey = (simpleClients.data as SimpleClient[]).map(
          (client: SimpleClient) => {
            const trimmedNo = String(client.no).replace(/^0+/, '')
            const sortkey = trimmedNo.padStart(20, '0')
            client.sortKey = sortkey

            return client
          }
        )

        const filteredDistinctClients = withAddedSortKey.filter((client) =>
          distinctClients.includes(client.id)
        )

        return {
          data:
            filteredDistinctClients.length > 0 ? filteredDistinctClients : [],
        }
      },
    }),

    /** Notifications */
    getNotifications: build.query<INotification[], void>({
      providesTags: [GET_USER_NOTIFICATIONS_TAG],
      async queryFn(_arg, _api, _extraOptions, query) {
        const userNotifications = await query({
          url: `${USER_SETTING_BASE_URL}/notifications`,
          method: 'GET',
          headers,
        })

        const allNotifications = await query({
          url: `${USER_SETTING_BASE_URL}/notifications/types`,
          method: 'GET',
          headers,
        })

        if (userNotifications.error || allNotifications.error) {
          return { error: userNotifications.error || allNotifications.error }
        }

        const mappedNotifications = mapNotifications(
          allNotifications.data as INotification[],
          userNotifications.data as IActiveNotification[]
        )

        return { data: mappedNotifications }
      },
    }),
    updateNotificationn: build.mutation<void, IHandleNotification>({
      async queryFn(arg, _api, _extraOptions, query) {
        const result = await query({
          url: `${USER_SETTING_BASE_URL}/notifications/${arg.id}`,
          method: arg.active ? 'POST' : 'DELETE',
          headers,
        })

        if (result.error) {
          return { error: arg.id }
        }

        userApi.util.updateQueryData(
          'getNotifications',
          undefined,
          (draft: INotification[]) => {
            const notification = draft.find((n) => n.id === arg.id)

            if (notification) {
              notification.active = arg.active
            }

            return draft
          }
        )

        return { data: undefined }
      },
      invalidatesTags: [GET_USER_NOTIFICATIONS_TAG],
    }),

    /** User settings */
    getUserSettings: build.query<IUserSettings, void>({
      providesTags: [GET_USER_SETTINGS_TAG],
      query: () => ({
        url: `${USER_SETTING_BASE_URL}/profile`,
        method: 'GET',
        headers,
      }),
    }),
    updateUserSettings: build.mutation<void, IHandleSetting>({
      query: (arg) => ({
        url: `${USER_SETTING_BASE_URL}/profile`,
        method: 'PUT',
        data: { settingName: arg.type, enabled: arg.active },
        headers,
      }),
      invalidatesTags: [GET_USER_SETTINGS_TAG],
    }),
  }),
})

export default userApi
export const {
  useLazyGetUserQuery,
  useUpdateUserSettingsMutation,
  useUpdateNotificationnMutation,
  useGetNotificationsQuery,
} = userApi
